import { FormEvent, useEffect, useMemo, useState } from 'react';
import { MapPin } from 'lucide-react';
import type { ApiLocation } from '../../services/api';
import {
  findLocationByCoordinates,
  loadMonitoringLocations,
  resolveLocation,
  toResolvedLocation,
  type ResolvedLocation,
} from '../../services/sensorBoxLocations';

type Mode = 'existing' | 'new';

type FieldErrors = {
  latitude?: string;
  longitude?: string;
  siteName?: string;
};

type LocationStepProps = {
  resolved: ResolvedLocation | null;
  onResolved: (location: ResolvedLocation) => void;
  onClear: () => void;
  onContinue?: () => void;
};

const inputClass =
  'w-full h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1';
const inputErrorClass = 'border-red-300 focus-visible:ring-red-400';

function parseCoordinate(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

export default function LocationStep({ resolved, onResolved, onClear, onContinue }: LocationStepProps) {
  const [mode, setMode] = useState<Mode>('existing');
  const [locations, setLocations] = useState<ApiLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [pickedId, setPickedId] = useState<string | null>(null);

  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [siteName, setSiteName] = useState('');
  const [siteDescription, setSiteDescription] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await loadMonitoringLocations();
        if (!cancelled) setLocations(data);
      } catch {
        if (!cancelled) {
          setLoadError('Could not load monitoring locations from the Seawall API.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredLocations = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return locations;
    return locations.filter((location) => {
      const haystack = [
        location.siteName,
        location.siteDescription,
        String(location.ID),
        String(location.gCoord.x),
        String(location.gCoord.y),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [locations, query]);

  const parsedLat = parseCoordinate(latitude);
  const parsedLng = parseCoordinate(longitude);
  const coordinateMatch =
    parsedLat !== null && parsedLng !== null
      ? findLocationByCoordinates(locations, parsedLat, parsedLng)
      : null;

  const switchMode = (next: Mode) => {
    setMode(next);
    setFormError(null);
    setFieldErrors({});
  };

  const confirmExisting = () => {
    const selected = locations.find((location) => String(location.ID) === pickedId);
    if (!selected) {
      setFormError('Select an existing location to continue.');
      return;
    }
    onResolved(toResolvedLocation(selected, true));
  };

  const validateNew = (): FieldErrors => {
    const next: FieldErrors = {};
    if (parsedLat === null) {
      next.latitude = 'Enter a valid latitude.';
    } else if (parsedLat < -90 || parsedLat > 90) {
      next.latitude = 'Latitude must be between -90 and 90.';
    }

    if (parsedLng === null) {
      next.longitude = 'Enter a valid longitude.';
    } else if (parsedLng < -180 || parsedLng > 180) {
      next.longitude = 'Longitude must be between -180 and 180.';
    }

    if (!siteName.trim() && !coordinateMatch) {
      next.siteName = 'Site name is required for a new location.';
    }

    return next;
  };

  const handleNewSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const nextErrors = validateNew();
    setFieldErrors(nextErrors);
    setFormError(null);
    if (Object.keys(nextErrors).length > 0) return;
    if (parsedLat === null || parsedLng === null) return;

    setSubmitting(true);
    try {
      const resolvedLocation = await resolveLocation(locations, {
        latitude: parsedLat,
        longitude: parsedLng,
        siteName: siteName.trim(),
        siteDescription: siteDescription.trim(),
      });
      onResolved(resolvedLocation);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Could not resolve this location.');
    } finally {
      setSubmitting(false);
    }
  };

  if (resolved) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-teal-700 mb-1">Location selected</p>
            <h2 className="text-2xl text-[#004F56] mb-2">{resolved.siteName}</h2>
            {resolved.siteDescription && (
              <p className="text-gray-600 mb-3">{resolved.siteDescription}</p>
            )}
            <dl className="grid sm:grid-cols-2 gap-2 text-sm text-gray-700">
              <div>
                <dt className="text-gray-500">Location ID</dt>
                <dd className="font-medium">{resolved.id}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Coordinates</dt>
                <dd className="font-medium">
                  {resolved.latitude}, {resolved.longitude}
                </dd>
              </div>
            </dl>
            <p className="mt-4 text-sm text-teal-700">
              {resolved.reused
                ? 'This existing location will be reused for the sensor box.'
                : 'This new location ID will be assigned to the sensor box.'}
            </p>
            {onContinue && (
              <button
                type="button"
                onClick={onContinue}
                className="mt-6 w-full h-11 rounded-lg bg-[#005E6E] text-white font-semibold hover:bg-[#004957] transition-colors shadow-md"
              >
                Continue to box
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClear}
            className="shrink-0 text-sm font-medium text-teal-700 hover:underline"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      <div className="grid grid-cols-2 border-b border-gray-100">
        <button
          type="button"
          onClick={() => switchMode('existing')}
          disabled={submitting}
          className={`py-4 text-sm font-semibold transition-colors disabled:opacity-60 ${
            mode === 'existing'
              ? 'text-teal-600 border-b-2 border-teal-500 bg-teal-50/50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Existing location
        </button>
        <button
          type="button"
          onClick={() => switchMode('new')}
          disabled={submitting}
          className={`py-4 text-sm font-semibold transition-colors disabled:opacity-60 ${
            mode === 'new'
              ? 'text-teal-600 border-b-2 border-teal-500 bg-teal-50/50'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          New coordinates
        </button>
      </div>

      <div className="p-6 sm:p-8">
        {loadError && (
          <div className="mb-5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
            {loadError}
          </div>
        )}

        {mode === 'existing' ? (
          <div className="space-y-4">
            <p className="text-gray-600 text-sm">
              Choose a monitoring site that already exists. The sensor box will use its location ID.
            </p>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by name, ID, or coordinates"
              className={inputClass}
              aria-label="Search locations"
            />

            {loading ? (
              <p className="text-sm text-gray-500 py-8 text-center">Loading locations…</p>
            ) : filteredLocations.length === 0 ? (
              <p className="text-sm text-gray-500 py-8 text-center">
                No locations match that search. Try new coordinates instead.
              </p>
            ) : (
              <ul className="space-y-2 max-h-80 overflow-y-auto">
                {filteredLocations.map((location) => {
                  const id = String(location.ID);
                  const selected = pickedId === id;
                  return (
                    <li key={id}>
                      <button
                        type="button"
                        onClick={() => {
                          setPickedId(id);
                          setFormError(null);
                        }}
                        className={`w-full text-left rounded-xl border px-4 py-3 transition-colors ${
                          selected
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-200 hover:border-teal-300 hover:bg-teal-50/40'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <MapPin className={`mt-0.5 h-4 w-4 shrink-0 ${selected ? 'text-teal-600' : 'text-gray-400'}`} />
                          <div>
                            <p className="font-medium text-gray-900">{location.siteName}</p>
                            {location.siteDescription && (
                              <p className="text-sm text-gray-600">{location.siteDescription}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              ID {id} · {location.gCoord.x}, {location.gCoord.y}
                            </p>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {formError && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
                {formError}
              </div>
            )}

            <button
              type="button"
              onClick={confirmExisting}
              disabled={!pickedId || loading}
              className="w-full h-11 rounded-lg bg-[#005E6E] text-white font-semibold hover:bg-[#004957] transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Use this location
            </button>
          </div>
        ) : (
          <form onSubmit={handleNewSubmit} noValidate className="space-y-5">
            <p className="text-gray-600 text-sm">
              Enter coordinates for the box. If that point already exists, the existing location ID
              is reused. Otherwise a new site is created.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="latitude" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Latitude
                </label>
                <input
                  id="latitude"
                  inputMode="decimal"
                  value={latitude}
                  onChange={(event) => setLatitude(event.target.value)}
                  placeholder="25.823004"
                  className={`${inputClass} ${fieldErrors.latitude ? inputErrorClass : ''}`}
                />
                {fieldErrors.latitude && (
                  <p className="mt-1.5 text-xs text-red-500">{fieldErrors.latitude}</p>
                )}
              </div>
              <div>
                <label htmlFor="longitude" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Longitude
                </label>
                <input
                  id="longitude"
                  inputMode="decimal"
                  value={longitude}
                  onChange={(event) => setLongitude(event.target.value)}
                  placeholder="-80.178207"
                  className={`${inputClass} ${fieldErrors.longitude ? inputErrorClass : ''}`}
                />
                {fieldErrors.longitude && (
                  <p className="mt-1.5 text-xs text-red-500">{fieldErrors.longitude}</p>
                )}
              </div>
            </div>

            {coordinateMatch && (
              <div className="rounded-lg bg-teal-50 border border-teal-200 text-teal-800 text-sm px-4 py-3">
                These coordinates match <span className="font-semibold">{coordinateMatch.siteName}</span>
                {' '}(ID {String(coordinateMatch.ID)}). Submitting will reuse that location.
              </div>
            )}

            <div>
              <label htmlFor="siteName" className="block text-sm font-medium text-gray-700 mb-1.5">
                Site name {coordinateMatch ? <span className="text-gray-400 font-normal">(optional)</span> : null}
              </label>
              <input
                id="siteName"
                value={siteName}
                onChange={(event) => setSiteName(event.target.value)}
                placeholder="Morningside Park"
                className={`${inputClass} ${fieldErrors.siteName ? inputErrorClass : ''}`}
              />
              {fieldErrors.siteName && (
                <p className="mt-1.5 text-xs text-red-500">{fieldErrors.siteName}</p>
              )}
            </div>

            <div>
              <label htmlFor="siteDescription" className="block text-sm font-medium text-gray-700 mb-1.5">
                Description <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                id="siteDescription"
                value={siteDescription}
                onChange={(event) => setSiteDescription(event.target.value)}
                rows={3}
                placeholder="Coastal monitoring station"
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-1"
              />
            </div>

            {formError && (
              <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3">
                {formError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || loading}
              className="w-full h-11 rounded-lg bg-[#005E6E] text-white font-semibold hover:bg-[#004957] transition-colors shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting
                ? coordinateMatch
                  ? 'Reusing location…'
                  : 'Creating location…'
                : coordinateMatch
                  ? 'Reuse matching location'
                  : 'Create location'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
